import {
  doc,
  onSnapshot,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { Survey, SurveyQuestion, SurveyStatus } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * 単一 survey doc の subscribe hook (Sprint T、user 第 16 弾)。
 *
 * `useSurveys` は一覧表示用の SurveySummary (questions[] を含まない) を返すが、
 * 回答 form (`SurveyForm`) では `questions[]` 完全データが必要なので本 hook で別途
 * 単一 doc を subscribe する。admin 第 22 弾の `mapSurvey` と同じ正規化を行う
 * (status / questions[] / collectParticipantProfile / allowEdit の defensive narrow)。
 *
 * 旧 v1 `apps/user/src/lib/surveys.ts` の `subscribeSurvey` 相当 (docs/97 §3 L3)。
 */

export type SurveyDetailState =
  | { status: 'loading'; survey: null; error: null }
  | { status: 'ready'; survey: Survey; error: null }
  | { status: 'not-found'; survey: null; error: null }
  | { status: 'error'; survey: null; error: Error };

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asStatus(value: unknown): SurveyStatus {
  return value === 'published' || value === 'closed' ? value : 'draft';
}

function asQuestions(value: unknown): SurveyQuestion[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (q): q is SurveyQuestion =>
      typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
  );
}

/** Firestore doc → Survey (admin useSurveys.mapSurvey と同じ正規化、user では片方向 read のみ)。 */
export function mapSurveyDetail(snap: { id: string; data: () => DocumentData }): Survey {
  const data = snap.data();
  const out: Survey = {
    id: snap.id,
    title: typeof data.title === 'string' ? data.title : '',
    description: typeof data.description === 'string' ? data.description : '',
    status: asStatus(data.status),
    questions: asQuestions(data.questions),
    collectParticipantProfile:
      typeof data.collectParticipantProfile === 'boolean' ? data.collectParticipantProfile : true,
    allowEdit: typeof data.allowEdit === 'boolean' ? data.allowEdit : true,
    schemaVersion: 1,
    responseCount: typeof data.responseCount === 'number' ? data.responseCount : 0,
    createdAt: tsToIso(data.createdAt),
    updatedAt: tsToIso(data.updatedAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
    version: typeof data.version === 'number' ? data.version : 0,
  };
  if (data.sourceMasterSurveyId !== undefined) {
    out.sourceMasterSurveyId =
      typeof data.sourceMasterSurveyId === 'string' ? data.sourceMasterSurveyId : null;
  }
  if (data.linkedToMaster !== undefined) {
    const v = data.linkedToMaster;
    if (v === 'linked' || v === 'forked' || v === 'local') out.linkedToMaster = v;
  }
  if (typeof data.masterVersion === 'number') out.masterVersion = data.masterVersion;
  return out;
}

export function useSurveyDetail(districtId: string, surveyId: string | null): SurveyDetailState {
  const [state, setState] = useState<SurveyDetailState>({
    status: 'loading',
    survey: null,
    error: null,
  });

  useEffect(() => {
    if (!surveyId) {
      setState({ status: 'loading', survey: null, error: null });
      return;
    }
    const ref = doc(db, 'districts', districtId, 'surveys', surveyId);
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setState({ status: 'not-found', survey: null, error: null });
          return;
        }
        setState({ status: 'ready', survey: mapSurveyDetail(snap), error: null });
      },
      (error) => setState({ status: 'error', survey: null, error }),
    );
    return () => unsub();
  }, [districtId, surveyId]);

  return state;
}
